import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, query, where, documentId, getDocs } from 'firebase/firestore';
import * as LucideIcons from 'lucide-react';

import { useAppContext } from '../AppContext';
import { useLocationFilter } from '../context/LocationFilterContext';
import LoadingSpinner from './LoadingSpinner';
import FeedSkeleton from './FeedSkeleton';
import AIGeneratedContentModal from './AIGeneratedContentModal';
import ProactiveSuggestionBanner from './ProactiveSuggestionBanner';
import CommentModal from './CommentModal';
import ReelContainer from './ReelContainer';
import ReelPost from './ReelPost';
import useOnScreen from '../hooks/useOnScreen';
import VisionCreator from './visions/VisionCreator';
import { ReportModal } from './ReportModal';

const MemoizedPost = React.memo(({ post, onVisible, ...props }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.5 });

    useEffect(() => {
        if (isVisible) {
            onVisible(props.index);
        }
    }, [isVisible, onVisible, props.index]);

    return (
        <div ref={ref} className="w-full h-full">
            <ReelPost post={post} {...props} />
        </div>
    );
});


function AnonymousFeed() {
    const { t, i18n } = useTranslation();
    const { userId, userProfiles, db, handleUserSelect, appFunctions, setMessage, appId, setIsMediaWhisperActive } = useAppContext();
    const { filterType, locationFilterRadius, userLocation, isLocationLoading, languagePreference, isProfileSyncComplete } = useLocationFilter();

    const [feedContent, setFeedContent] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showNewWhispersButton, setShowNewWhispersButton] = useState(false);
    
    const isFetching = useRef(false);
    const CACHE_KEY = `anonymousFeedCache_${userId || 'anonymous'}`;

    const [aiModalContent, setAiModalContent] = useState(null);
    const [aiModalTitle, setAiModalTitle] = useState('');
    const [commentModalPost, setCommentModalPost] = useState(null);
    const [visionCreatorOpen, setVisionCreatorOpen] = useState(false);
    const [sharedContent, setSharedContent] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportContent, setReportContent] = useState(null);

    const filteredFeed = useMemo(() => {
        return feedContent.filter(post => {
            const author = userProfiles.find(p => p.id === post.authorId);
            if (author?.isAI) return true; // Always include AI users

            if (filterType === 'local' && userLocation) {
                if (!author?.location) return false;
                const distance = getDistance(userLocation, author.location);
                return distance <= locationFilterRadius;
            }
            if (filterType === 'country') {
                return author?.country === userProfiles.find(p => p.id === userId)?.country;
            }
            // Global filter has no location restrictions
            return true;
        });
    }, [feedContent, userProfiles, filterType, userLocation, locationFilterRadius, userId]);

    useEffect(() => {
        if (filteredFeed.length > 0 && currentIndex < filteredFeed.length) {
            const currentPost = filteredFeed[currentIndex];
            const hasMedia = currentPost.mediaUrl || (currentPost.mediaUrls && currentPost.mediaUrls.length > 0);
            setIsMediaWhisperActive(hasMedia);
        } else {
            setIsMediaWhisperActive(false);
        }
        // Cleanup when component unmounts
        return () => {
            setIsMediaWhisperActive(false);
        };
    }, [currentIndex, filteredFeed, setIsMediaWhisperActive]);
    
    const fetchFeedContent = useCallback(async (loadMore = false, isBackgroundRefresh = false) => {
        if (isFetching.current || (loadMore && !hasMore) || isLocationLoading || !isProfileSyncComplete) return;

        isFetching.current = true;
        if (loadMore) setIsLoadingMore(true);
        else if (!isBackgroundRefresh) setIsLoading(true);
        setError(null);

        try {
            const getFeed = httpsCallable(appFunctions, 'getAnonymousFeed');
            const result = await getFeed({
                lastVisible: loadMore ? lastVisible : null,
                filterType,
                userLocation,
                locationFilterRadius,
                languagePreference: {
                    enabled: languagePreference,
                    language: i18n.language
                }
            });

            const { feedItems, lastVisible: newLastVisible } = result.data;

            if (feedItems && feedItems.length > 0) {
                const contentIds = feedItems.map(item => item.contentId);
                const contentQuery = query(collection(db, `artifacts/${appId}/public/data/whispers`), where(documentId(), 'in', contentIds));
                const snapshot = await getDocs(contentQuery);
                const contentMap = new Map(snapshot.docs.map(doc => [doc.id, { id: doc.id, ...doc.data(), contentType: 'whisper' }]));
                const newContent = contentIds.map(id => contentMap.get(id)).filter(Boolean);

                if (isBackgroundRefresh) {
                    const currentFeedIds = new Set(feedContent.map(p => p.id));
                    const trulyNewContent = newContent.filter(p => !currentFeedIds.has(p.id));
                    if (trulyNewContent.length > 0) {
                        setShowNewWhispersButton(true);
                    }
                } else {
                    setFeedContent(prev => loadMore ? [...prev, ...newContent] : newContent);
                    if (!loadMore) {
                        localStorage.setItem(CACHE_KEY, JSON.stringify(newContent.slice(0, 10)));
                    }
                }
                
                setLastVisible(newLastVisible);
                setHasMore(feedItems.length > 0);
            } else {
                setHasMore(false);
                if (!loadMore) setFeedContent([]);
            }
        } catch (err) {
            console.error("Error fetching anonymous feed:", err);
            setError(t('anonymousFeed.loadFeedError'));
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
            isFetching.current = false;
        }
    }, [
        hasMore, isLocationLoading, appFunctions, lastVisible, filterType, userLocation, 
        locationFilterRadius, languagePreference, i18n.language, db, appId, t, CACHE_KEY, feedContent
    ]);

    // Initial fetch and re-fetch on filter change
    useEffect(() => {
        try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (cachedData) {
                setFeedContent(JSON.parse(cachedData));
                setIsLoading(false);
                fetchFeedContent(false, true); // Background refresh
            } else {
                fetchFeedContent(false);
            }
        } catch (e) {
            console.error("Failed to read cache", e);
            fetchFeedContent(false);
        }
    }, [filterType, locationFilterRadius, languagePreference]);

    const refreshFeed = () => {
        setShowNewWhispersButton(false);
        setFeedContent([]);
        setLastVisible(null);
        setHasMore(true);
        fetchFeedContent(false);
    };

    // Helper function for distance calculation (can be moved to a utils file)
    function getDistance(loc1, loc2) {
        const R = 6371; // Radius of the Earth in km
        const dLat = (loc2.latitude - loc1.latitude) * Math.PI / 180;
        const dLon = (loc2.longitude - loc1.longitude) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(loc1.latitude * Math.PI / 180) * Math.cos(loc2.latitude * Math.PI / 180) *
                  Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const loadMoreContent = useCallback(() => {
        if (!isLoading && hasMore) {
            fetchFeedContent(true);
        }
    }, [isLoading, hasMore, fetchFeedContent]);

    useEffect(() => {
        if (!isLoading && hasMore && feedContent.length > 0 && currentIndex >= filteredFeed.length - 2) {
            loadMoreContent();
        }
    }, [currentIndex, filteredFeed.length, hasMore, isLoading, loadMoreContent]);

    const handleShareToVision = useCallback((post) => {
        setSharedContent(post);
        setVisionCreatorOpen(true);
    }, []);

    const handleShowComments = useCallback((post) => {
        setCommentModalPost(post);
    }, []);
    
    const handleAmplify = useCallback(async (entryId) => {
        if (!userId) { setMessage(t('anonymousFeed.signInRequired')); return; }
        try {
            const amplifyWhisper = httpsCallable(appFunctions, 'amplifyWhisper');
            await amplifyWhisper({ whisperId: entryId, amount: 10 });
        } catch (error) { console.error("Error amplifying whisper:", error); setMessage(t('anonymousFeed.amplifyError')); }
    }, [userId, appFunctions, setMessage, t]);

    const handleDelete = useCallback(async (entryId) => {
        if (!userId) { setMessage(t('anonymousFeed.signInRequired')); return; }
        try {
            const deleteContent = httpsCallable(appFunctions, 'deleteContent');
            await deleteContent({ contentId: entryId, contentType: 'whisper' });
        } catch (error) { console.error("Error deleting whisper:", error); setMessage(t('anonymousFeed.deleteError')); }
    }, [userId, appFunctions, setMessage, t]);

    const handleSuperAmplify = useCallback(async (entryId) => {
        if (!userId) { setMessage(t('anonymousFeed.signInRequired')); return; }
        try {
            const superAmplifyWhisper = httpsCallable(appFunctions, 'superAmplifyWhisper');
            await superAmplifyWhisper({ whisperId: entryId });
        } catch (error) { console.error("Error super amplifying whisper:", error); setMessage(t('anonymousFeed.superAmplifyError', { error: error.message })); }
    }, [userId, appFunctions, setMessage, t]);

    const getUserDisplayName = useCallback((authorId) => userProfiles.find(p => p.id === authorId)?.displayName || t('anonymousFeed.anonymousUser'), [userProfiles, t]);

    const handleLikeToggle = useCallback(async (entryId, authorId) => {
        if (!userId) { setMessage(t('anonymousFeed.signInRequired')); return; }
        
        const originalFeed = [...feedContent];
        
        // Optimistic update
        setFeedContent(prevFeed => prevFeed.map(post => {
            if (post.id === entryId) {
                const likes = post.likes || [];
                const hasLiked = likes.includes(userId);
                const newLikes = hasLiked ? likes.filter(id => id !== userId) : [...likes, userId];
                return { ...post, likes: newLikes, likesCount: newLikes.length };
            }
            return post;
        }));

        try {
            const updateReaction = httpsCallable(appFunctions, 'updateReaction');
            await updateReaction({ entryId: entryId, authorId: authorId, reaction: 'like', contentType: 'whisper' });
        } catch (error) {
            console.error("Error syncing 'like' reaction:", error);
            setMessage(t('anonymousFeed.likeError'));
            setFeedContent(originalFeed); // Revert on failure
        }
    }, [userId, appFunctions, setMessage, t, feedContent]);

    const handleDislikeToggle = useCallback(async (entryId, authorId) => {
        if (!userId) { setMessage(t('anonymousFeed.signInRequired')); return; }

        const originalFeed = [...feedContent];

        // Optimistic update
        setFeedContent(prevFeed => prevFeed.map(post => {
            if (post.id === entryId) {
                const dislikes = post.dislikes || [];
                const hasDisliked = dislikes.includes(userId);
                const newDislikes = hasDisliked ? dislikes.filter(id => id !== userId) : [...dislikes, userId];
                return { ...post, dislikes: newDislikes, dislikesCount: newDislikes.length };
            }
            return post;
        }));

        try {
            const updateReaction = httpsCallable(appFunctions, 'updateReaction');
            await updateReaction({ entryId: entryId, authorId: authorId, reaction: 'dislike', contentType: 'whisper' });
        } catch (error) {
            console.error("Error syncing 'dislike' reaction:", error);
            setMessage(t('anonymousFeed.dislikeError'));
            setFeedContent(originalFeed); // Revert on failure
        }
    }, [userId, appFunctions, setMessage, t, feedContent]);

    const handleReport = useCallback((post) => {
        setReportContent({
            contentId: post.id,
            contentType: post.contentType || 'whisper',
            authorId: post.authorId
        });
        setShowReportModal(true);
    }, []);

    const aiAnalysisHandler = (toolType, responseData) => {
        const title = toolType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
        setAiModalTitle(title);
        setAiModalContent(responseData.text);
    };

    const handleVisibilityChange = useCallback((index) => {
        setCurrentIndex(index);
    }, []);

    if (isLoading && filteredFeed.length === 0) {
        return <FeedSkeleton />;
    }

    if (error) {
        return (
            <div className="text-center text-red-300 p-8 bg-red-900/30 rounded-lg">
                <LucideIcons.WifiOff size={48} className="mx-auto mb-4" />
                <h3 className="text-xl font-bold">{t('anonymousFeed.connectionError')}</h3>
                <p className="mt-2">{error}</p>
            </div>
        );
    }

    return (
        <div className="md:col-span-2 p-4 sm:p-6 rounded-lg shadow-lg text-white h-full flex flex-col">
            {showReportModal && <ReportModal {...reportContent} onClose={() => setShowReportModal(false)} />}
            
            {showNewWhispersButton && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50">
                    <button 
                        onClick={refreshFeed}
                        className="bg-blue-600/90 backdrop-blur-md hover:bg-blue-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg transition-all transform hover:scale-105 active:scale-95 border border-blue-400/30 flex items-center"
                    >
                        <LucideIcons.RefreshCw size={14} className="mr-1.5 animate-spin-slow" />
                        {t('anonymousFeed.newWhispers')}
                    </button>
                </div>
            )}

            <div className="flex-grow min-h-0 relative">
                {commentModalPost && <CommentModal post={commentModalPost} onClose={() => setCommentModalPost(null)} contentType={commentModalPost.contentType} nexusId={commentModalPost.nexusId || null} />}
                {aiModalContent && <AIGeneratedContentModal title={aiModalTitle} content={aiModalContent} onClose={() => setAiModalContent(null)} LucideIcons={LucideIcons} />}
                
                {filteredFeed.length > 0 ? (
                    <ReelContainer>
                        {filteredFeed.map((e, index) => (
                            <div key={e.id} style={{ height: '100%', scrollSnapAlign: 'start' }}>
                                <MemoizedPost
                                    post={e}
                                    context="feed"
                                    index={index}
                                    userId={userId}
                                    userProfiles={userProfiles}
                                    getUserDisplayName={getUserDisplayName}
                                    onUserSelect={handleUserSelect}
                                    handleLikeToggle={handleLikeToggle}
                                    handleDislikeToggle={handleDislikeToggle}
                                    onShowComments={handleShowComments}
                                    handleAmplify={handleAmplify}
                                    handleSuperAmplify={handleSuperAmplify}
                                    handleDelete={handleDelete}
                                    handleAiAnalysis={aiAnalysisHandler}
                                    onShareToVision={handleShareToVision}
                                    onVisible={handleVisibilityChange}
                                    onReport={handleReport}
                                />
                            </div>
                        ))}
                         {isLoadingMore && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1-2 z-10">
                                <LoadingSpinner />
                            </div>
                        )}
                    </ReelContainer>
                ) : (
                    <div className="text-center text-gray-300 p-8 bg-gray-800/50 rounded-lg">
                        <LucideIcons.Wind size={48} className="mx-auto mb-4 text-blue-400" />
                        <h3 className="text-xl font-bold">{t('anonymousFeed.emptyTitle')}</h3>
                        <p className="mt-2">{t('anonymousFeed.emptyDescription')}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AnonymousFeed;